import React, {
  createContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import Cookie from "../utils/Cookie";
import SplashScreen from "../components/SplashScreen";
import { jwtDecode } from "jwt-decode";
import {
  CMS_BASE_URL,
  EMAIL_VERIFICATION_OTP,
  LOGIN,
  LOGOUT,
  LOGOUT_USER,
  ME,
  SIGN_UP,
  SUPER_LOGIN,
} from "../utils/APIEndPoints";
import { CallAPI } from "../utils/apiActions";
import { useLocation, useNavigate } from "react-router-dom";
import { showToast } from "../helper/toastHelper";
import { environment, LABEL, siteURLs } from "../utils/constant";
import useDashboard from "../hooks/useDashboard";
import io from "socket.io-client";
import { handleNavigate, handleRestriction } from "../utils/common";
import {
  clearEmailVerificationFlowStorage,
  clearPendingEmailVerificationPlan,
  getPendingEmailVerificationPlan,
  setPendingEmailVerificationPlan,
} from "../utils/emailVerificationFlow";

const LOCATION_CACHE_KEYS = {
  country: "country",
  currency: "currency",
  updatedAt: "locationUpdatedAt",
};

const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;

const normalizeSocketBaseUrl = (value) => {
  if (!value || typeof value !== "string") {
    return "";
  }

  return value.trim().replace(/\/+$/, "");
};

const resolveTakeoverSocketConfig = () => {
  const socketBaseUrl = normalizeSocketBaseUrl(
    process.env.REACT_APP_AGENT_SOCKET_URL || window.location.origin,
  );

  let socketPath = process.env.REACT_APP_AGENT_SOCKET_PATH?.trim();

  if (!socketPath) {
    try {
      const { hostname } = new URL(socketBaseUrl);
      const isLocalHost =
        hostname === "localhost" || hostname === "127.0.0.1";

      socketPath = isLocalHost ? "/socket.io" : "/user/socket.io";
    } catch (error) {
      socketPath = "/user/socket.io";
    }
  }

  return {
    socketUrl: `${socketBaseUrl}/human-socket`,
    socketPath,
  };
};

/**
 * Initial state for the authentication context.
 * @type {Object}
 * @property {Object|null} subscriptionPayload - Payload for subscription creation.
 * @property {string|boolean} isEmailVerified - Status of email verification ('NA', true, false).
 * @property {boolean} isAuthenticated - Whether the user is authenticated.
 * @property {boolean} isLogout - Whether the user is logged out.
 * @property {boolean} isInitialised - Whether the authentication state has been initialised.
 * @property {Object|null} user - The authenticated user object.
 * @property {boolean} loader - Global loader state.
 * @property {boolean} deleteLoader - Loader state for account deletion.
 * @property {number|null} accessType - Access type of the user.
 * @property {Object|null} companyInfo - Company information.
 * @property {Array} liveChats - List of live chats.
 * @property {Object|null} takeOverSocket - Socket connection for human takeover.
 * @property {boolean} isChatRequestAvailable - Whether a chat request is available.
 * @property {Object|null} takeOverChat - Current chat being taken over.
 * @property {boolean} isRazorPayStart - Whether Razorpay payment has started.
 * @property {string|undefined} defaultCountry - Default country based on IP.
 * @property {string|undefined} defaultCurrency - Default currency based on IP.
 */
const initialAuthState = {
  subscriptionPayload: null,
  isEmailVerified: "NA",
  isAuthenticated: false,
  isLogout: false,
  isInitialised: false,
  user: null,
  loader: false,
  deleteLoader: false,
  accessType: null,
  companyInfo: null,
  liveChats: [],
  takeOverSocket: null,
  isChatRequestAvailable: false,
  takeOverChat: null,
  isRazorPayStart: false,
  defaultCountry: undefined,
  defaultCurrency: undefined,
};

/**
 * Reducer function for managing authentication state.
 * @param {Object} state - Current state.
 * @param {Object} action - Action object with type and payload.
 * @returns {Object} New state.
 */
const reducer = (state, action) => {
  // console.log(action.payload, "action.payload");
  switch (action.type) {
    case "RESET_STATE": {
      return {
        ...state,
        ...initialAuthState,
        isInitialised: true,
      };
    }
    case "INITIALISE": {
      const { isAuthenticated, user } = action.payload;
      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        user: user,
      };
    }
    case "SET_LOCATION": {
      const { defaultCountry, defaultCurrency } = action.payload;
      return {
        ...state,
        defaultCountry: defaultCountry,
        defaultCurrency: defaultCurrency,
      };
    }
    case "LOGIN": {
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload,
      };
    }
    case "LOGOUT": {
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        accessType: null,
      };
    }
    case "USER_UPDATE": {
      return {
        ...state,
        user: action.payload,
        accessType: action.accessType,
        isAuthenticated: true,
        isInitialised: true,
      };
    }
    case "LOADER_UPDATE": {
      return {
        ...state,
        loader: action.payload,
      };
    }
    case "REGISTER": {
      const { user } = action.payload;
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload,
      };
    }
    case "EMAIL_VERIFIED": {
      return {
        ...state,
        isEmailVerified: action.payload,
      };
    }
    case "CREATE_SUBSCRIPTION_PAYLOAD": {
      return {
        ...state,
        subscriptionPayload: action.payload,
      };
    }
    case "SET_COMPANY_INFO": {
      return {
        ...state,
        companyInfo: action.payload,
      };
    }
    case "SET_LIVE_CHAT": {
      return {
        ...state,
        liveChats: action.payload,
      };
    }
    case "TAKEOVER_SOCKET_CONNECTION": {
      return {
        ...state,
        takeOverSocket: action.payload,
      };
    }
    case "SET_CHAT_REQUEST_AVAILABLE": {
      return {
        ...state,
        isChatRequestAvailable: action.payload,
      };
    }
    case "SET_TAKEOVER_CHAT": {
      return {
        ...state,
        takeOverChat: action.payload,
      };
    }
    case "SET_IS_RAZOR_PAY_START": {
      return {
        ...state,
        isRazorPayStart: action.payload,
      };
    }
    default: {
      return { ...state };
    }
  }
};

/**
 * Fetches IP information to determine country and currency.
 * @returns {Promise<Object>} Object containing countryName and currency code.
 */
async function getIpInfo() {
  console.log("process.env.NODE_ENV", process.env.NODE_ENV);
  if (process.env.NODE_ENV == "development") {
    return {
      countryName: "India",

      currency: {
        code: "INR",
      },
    };
  }

  try {
    // Set endpoint and your access key
    // var accessKey = "0d99b4d0-34f6-4bf2-a92c-83049dbecc18";
    // var accessKey = "f43b0ad6-afdd-436f-af3c-c9ccbf4f6d59";
    const url = `${process.env.REACT_APP_CHAT_BASE_URL}visitor-location`;
    // var url = "https://apiip.net/api/check?accessKey=" + accessKey;
    // Make a request and store the response
    const response = await fetch(url);
    // Decode JSON response:
    const result = await response.json();
    // Output the "code" value inside "currency" object
    return result;
  } catch (error) {
    return {
      countryName: "India",

      currency: {
        code: "INR",
      },
    };
  }
}

function getStoredLocation() {
  return {
    country: localStorage.getItem(LOCATION_CACHE_KEYS.country),
    currency: localStorage.getItem(LOCATION_CACHE_KEYS.currency),
    updatedAt: localStorage.getItem(LOCATION_CACHE_KEYS.updatedAt),
  };
}

function setStoredLocation(country, currency) {
  localStorage.setItem(LOCATION_CACHE_KEYS.country, country);
  localStorage.setItem(LOCATION_CACHE_KEYS.currency, currency);
  localStorage.setItem(LOCATION_CACHE_KEYS.updatedAt, Date.now().toString());
}

function isStoredLocationFresh(updatedAt) {
  if (!updatedAt) {
    return false;
  }

  const updatedAtTime = Number(updatedAt);

  if (Number.isNaN(updatedAtTime)) {
    return false;
  }

  return Date.now() - updatedAtTime <= ONE_DAY_IN_MS;
}

/**
 * Context for authentication.
 */
const AuthContext = createContext({
  ...initialAuthState,
  login: () => Promise.resolve(),
  register: () => Promise.resolve(),
  logout: () => {},
  updateUser: () => Promise.resolve(),
  updateLoader: () => Promise.resolve(),
  updateEmailVerified: () => Promise.resolve(),
  createSubscriptionPayload: () => Promise.resolve(),
  updateCompanyInfo: () => Promise.resolve(),
  updateIsRazorPayStart: () => Promise.resolve(),
  handleSetLiveChat: () => Promise.resolve(),
  updateTakeOverSocketConnection: () => Promise.resolve(),
  updateChatRequestAvailable: () => Promise.resolve(),
  updateSetTakeOverChat: () => Promise.resolve(),
  me: () => Promise.resolve(),
});

/**
 * Provider component for authentication context.
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - Child components.
 * @returns {JSX.Element} The AuthContext Provider.
 */
export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  const { planPurchase, setPlanPurchase } = useDashboard();
  const currentLocation = location.pathname;
  const takeoverSocketRef = useRef(null);
  const hasReceivedInitialChatListRef = useRef(false);
  const liveChatIdsRef = useRef([]);

  /**
   * Fetches current user details.
   * @param {Function} [cb] - Optional callback function to execute on success.
   * @returns {Promise<Object|void>} Login data or void.
   */
  const me = async (cb) => {
    try {
      const response = await CallAPI(
        "USER",
        "GET",
        ME,
        undefined,
        false,
        false,
      );

      if (response?.msg === "Server Error: 401") {
        dispatch({
          type: "RESET_STATE",
          payload: undefined,
        });
        navigate("/");
        return;
      }

      if (response?.res?.data?.status === 200) {
        const userData = response.res.data.data;

        if (userData.userType !== 1 && !userData.isEmailVerified) {
          const pendingPlan = getPendingEmailVerificationPlan();

          await CallAPI("USER", "POST", EMAIL_VERIFICATION_OTP, {
            email: userData.email,
            actionType: "post_signup",
          });

          updateEmailVerified(false);
          localStorage.setItem("email-verify", userData.email);
          localStorage.setItem(
            "email-verify-expiration",
            new Date(
              new Date().setMinutes(new Date().getMinutes() + 5),
            ).getTime(),
          );
          localStorage.setItem("action-type", "post_signup");
          dispatch({
            type: "INITIALISE",
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
          if (pendingPlan?.planId) {
            const params = new URLSearchParams({
              userId: userData.id,
              planId: pendingPlan.planId,
              promotionCode: pendingPlan.promotionCode || "",
              returnUrl: pendingPlan.returnUrl || "",
            }).toString();

            navigate(`/email-verification?${params}`);
          } else {
            navigate("/email-verification");
          }
        } else {
          console.log("here");
          // handleUserNavigation(userData);
          clearEmailVerificationFlowStorage({ clearPendingPlan: true });

          if (userData.parentType === 3) {
            localStorage.setItem("isAuth", true);
            localStorage.setItem(
              "parentUserRefferId",
              userData.parentUserRefferId,
            );
          } else {
            localStorage.removeItem("parentUserRefferId");
          }

          dispatch({
            type: "USER_UPDATE",
            payload: userData,
            accessType:
              userData.userType === 6 ? userData.parentType : userData.userType,
          });
        }

        if (cb) cb();
      } else {
        return { loginData: response?.msg || "Unknown error", response: false };
      }
    } catch (error) {
      console.error("Error in me function:", error);
      return { loginData: "Error fetching user data", response: false };
    }
  };

  /**
   * Handles navigation logic based on user type and subscription status.
   * @param {Object} userData - The user data object.
   */
  const handleUserNavigation = (userData) => {
    if (
      userData.userType === 2 &&
      userData.subscriptionStatus === 0 &&
      location.pathname !== "/payment-status"
    ) {
      navigate("/plan-list");
    } else if (
      userData.userType === 5 &&
      userData?.isAccountOnboarding !== 1 &&
      location.pathname !== "/payment-management"
    ) {
      navigate("/affiliate/payment-management");
    } else if (userData.userType === 3) {
      if (
        userData.subscriptionStatus === 0 &&
        location.pathname !== "/agency/payment-status"
      ) {
        navigate("/agency/plan-list");
      } else if (!userData.logoLink) {
        navigate("/agency/branding-management");
      }
    }
  };

  /**
   * Logs in a user.
   * @param {Object} body - Login credentials (email, password).
   * @param {Object|string} [plan] - Plan details if logging in during purchase.
   * @param {string} [promoCode=""] - Promo code if applicable.
   * @returns {Promise<Object>} Login result with success status and message.
   */
  const login = async (body, plan, promoCode = "") => {
    updateLoader(true);

    try {
      const response = await CallAPI("USER", "POST", LOGIN, body, false, false);
      updateLoader(false);

      if (response.status) {
        localStorage.removeItem("referral");
        localStorage.setItem("accessToken", response.res.data?.data?.token);
        const userData = response.res.data.data;

        if (userData.userType !== 1 && !userData.isEmailVerified) {
          await handleUnverifiedEmailLogin(body, userData, plan);
        } else {
          await handleVerifiedUserLogin(userData);
        }

        return { loginData: response.msg, response: true };
      } else {
        return { loginData: response.msg, response: false };
      }
    } catch (error) {
      updateLoader(false);
      console.error("Login error:", error);
      return { loginData: "Login failed", response: false };
    }
  };

  /**
   * Handles login flow for unverified emails.
   * @param {Object} body - Login credentials.
   * @param {Object} userData - User data from login response.
   * @param {Object} [plan] - Plan details.
   */
  const handleUnverifiedEmailLogin = async (body, userData, plan) => {
    updateLoader(true);
    await CallAPI("USER", "POST", EMAIL_VERIFICATION_OTP, {
      email: body.email,
      actionType: "post_signup",
    });
    updateLoader(false);

    updateEmailVerified(false);
    localStorage.setItem("email-verify", body.email);
    localStorage.setItem("action-type", "post_signup");
    localStorage.setItem(
      "email-verify-expiration",
      new Date(new Date().setMinutes(new Date().getMinutes() + 5)).getTime(),
    );

    if (plan?.id && !plan?.isFreePlan) {
      const subscriptionPayload = {
        userId: userData.id,
        planId: plan.id,
        promotionCode:
          userData?.promoCode && !userData?.isUsedPromoCode
            ? userData.promoCode
            : "",
        returnUrl: `${process.env.REACT_APP_FRONTEND_BASE_URL}payment-status`,
      };

      setPendingEmailVerificationPlan(subscriptionPayload);

      const params = new URLSearchParams(subscriptionPayload).toString();
      navigate(`/email-verification?${params}`);
      return;
    }

    clearPendingEmailVerificationPlan();
    navigate("/email-verification");
  };

  /**
   * Handles login flow for verified users.
   * @param {Object} userData - User data from login response.
   */
  const handleVerifiedUserLogin = (userData) => {
    // handleUserNavigation(userData);
    clearEmailVerificationFlowStorage({ clearPendingPlan: true });

    if (userData.parentType === 3) {
      localStorage.setItem("isAuth", true);
      localStorage.setItem("parentUserRefferId", userData.parentUserRefferId);
    } else {
      localStorage.removeItem("parentUserRefferId");
    }

    dispatch({
      type: "USER_UPDATE",
      payload: userData,
      accessType:
        userData.userType === 6 ? userData.parentType : userData.userType,
    });
  };

  /**
   * Performs super admin login.
   * @param {Object} body - Login credentials.
   * @returns {Promise<Object>} Login result.
   */
  const superLogin = async (body) => {
    updateLoader(true);

    try {
      const response = await CallAPI("USER", "POST", SUPER_LOGIN, body);
      updateLoader(false);

      if (response.status) {
        localStorage.removeItem("referral");
        localStorage.setItem("accessToken", response.res.data?.data?.token);
        me();
      } else {
        return { loginData: response.msg, response: false };
      }
    } catch (error) {
      updateLoader(false);
      console.error("Super login error:", error);
      return { loginData: "Super login failed", response: false };
    }
  };

  /**
   * Registers a new user.
   * @param {Object} payload - Registration data including plan details.
   * @returns {Promise<Object>} Registration result with success status and message.
   */
  const register = async (payload) => {
    const { selectedPlan, ...regformObj } = payload;

    console.log("Selected Plan Object:", selectedPlan);
    console.log("Remaining Fields Object:", regformObj);

    updateLoader(true);

    try {
      const response = await CallAPI(
        "USER",
        "POST",
        SIGN_UP,
        {
          ...regformObj,
          country: localStorage.getItem("country") ?? "India",
          currency: localStorage.getItem("currency") ?? "INR",
        },
        false,
        false,
      );

      updateLoader(false);
      console.log("Registration Response:", response);

      if (response?.res?.status === 201) {
        showToast(response.msg, LABEL.TOAST_TYPE.SUCCESS);
        const loginBody = { email: payload.email, password: payload.password };

        await login(loginBody, selectedPlan, payload?.promoCode || "");

        return { success: true, msg: "Registration and login successful." };
      } else {
        return { success: false, msg: response?.msg ?? "Server error" };
      }
    } catch (error) {
      updateLoader(false);
      console.error("Register User Error:", error);
      showToast(LABEL.SIGNUP.ERROR_MESSAGE.FAILED, LABEL.TOAST_TYPE.ERROR);
      return { success: false, msg: error?.message ?? "Server error" };
    }
  };

  const hasHumanTakeoverMenu = state.user?.menus?.some(
    (menu) => menu.menuName === "human-takeover",
  );

  useEffect(() => {
    if (!state.isAuthenticated || !state.user?.userUuid || !hasHumanTakeoverMenu) {
      if (takeoverSocketRef.current) {
        takeoverSocketRef.current.disconnect();
        takeoverSocketRef.current = null;
      }

      liveChatIdsRef.current = [];
      hasReceivedInitialChatListRef.current = false;
      updateTakeOverSocketConnection(null);
      return;
    }

    if (takeoverSocketRef.current) {
      takeoverSocketRef.current.disconnect();
    }

    hasReceivedInitialChatListRef.current = false;

    const normalizeLiveChatPayload = (payload) => {
      if (Array.isArray(payload)) {
        if (payload[0] === "chatList" && Array.isArray(payload[1])) {
          return payload[1];
        }

        if (payload.length === 1 && Array.isArray(payload[0])) {
          return payload[0];
        }

        return payload;
      }

      if (Array.isArray(payload?.chatList)) {
        return payload.chatList;
      }

      if (Array.isArray(payload?.data)) {
        return payload.data;
      }

      return [];
    };

    const getLiveChatIds = (chatList = []) =>
      chatList
        .map(
          (chatItem) =>
            chatItem?.chatlistId ||
            chatItem?.ChatList?.id ||
            chatItem?.ChatList?.chatId ||
            chatItem?.chatId,
        )
        .filter(Boolean);

    const syncLiveChatList = (socketPayload, options = {}) => {
      const { showIncomingToast = false, notificationMessage = "" } = options;
      const normalizedChatList = normalizeLiveChatPayload(socketPayload);
      const nextChatIds = getLiveChatIds(normalizedChatList);
      const previousChatIds = liveChatIdsRef.current;
      const newChatCount = nextChatIds.filter(
        (chatId) => !previousChatIds.includes(chatId),
      ).length;

      handleSetLiveChat(normalizedChatList);
      liveChatIdsRef.current = nextChatIds;

      if (!hasReceivedInitialChatListRef.current) {
        hasReceivedInitialChatListRef.current = true;
        return;
      }

      if (!showIncomingToast || (newChatCount === 0 && !notificationMessage)) {
        return;
      }

      showToast(
        notificationMessage ||
          `${newChatCount} new human takeover request${
            newChatCount > 1 ? "s" : ""
          } received.`,
        LABEL.TOAST_TYPE.INFO,
      );
    };

    const { socketUrl, socketPath } = resolveTakeoverSocketConfig();

    const socket = io(socketUrl, {
      path: socketPath,
      transports: ["websocket", "polling"],
      query: { id: state.user.userUuid },
      reconnection: true,
    });

    takeoverSocketRef.current = socket;
    updateTakeOverSocketConnection(socket);

    const handleChatList = (...args) => {
      const socketPayload = args.length === 1 ? args[0] : args;

      syncLiveChatList(socketPayload, { showIncomingToast: true });
      console.log("socket message", socketPayload);
    };

    const handleNotification = (payload) => {
      const notificationMessage =
        (typeof payload === "string" && payload) ||
        payload?.message ||
        payload?.title ||
        payload?.body ||
        "";

      syncLiveChatList(payload, {
        showIncomingToast: true,
        notificationMessage,
      });
    };

    const handleChatUpdateError = (payload) => {
      const errorMessage =
        payload?.error || "Unable to update human takeover chat.";
      showToast(errorMessage, LABEL.TOAST_TYPE.ERROR);
    };

    socket.on("chatList", handleChatList);
    socket.on("notification", handleNotification);
    socket.on("chatUpdateError", handleChatUpdateError);
    socket.on("connect_error", (error) => {
      console.error("Human takeover socket connection error:", error);
    });

    return () => {
      socket.off("chatList", handleChatList);
      socket.off("notification", handleNotification);
      socket.off("chatUpdateError", handleChatUpdateError);
      socket.disconnect();

      if (takeoverSocketRef.current === socket) {
        takeoverSocketRef.current = null;
      }

      updateTakeOverSocketConnection(null);
    };
  }, [state.isAuthenticated, state.user?.userUuid, hasHumanTakeoverMenu]);

  /**
   * Deletes the current user's account.
   */
  const deleteAccount = async () => {
    updateLoader(true);

    try {
      const res = await CallAPI("USER", "DELETE", "user/delete");
      if (res.res.data.status === 200) {
        await logout();
        showToast(res.res.data?.message, LABEL.TOAST_TYPE.SUCCESS);
      }
    } catch (err) {
      console.error("Delete account error:", err);
    } finally {
      updateLoader(false);
    }
  };

  /**
   * Logs out the current user.
   * @param {Object} [payload] - Optional payload for logout API.
   * @param {number} [usertype=2] - User type.
   */
  const logout = async (payload, usertype = 2) => {
    state.takeOverSocket?.disconnect();

    try {
      await CallAPI("USER", "POST", LOGOUT_USER, payload, null, false);
    } catch (error) {
      console.error("Logout API call failed:", error);
    }

    sessionStorage.clear();

    if (localStorage.getItem("isAuth") && localStorage.getItem("agency")) {
      localStorage.setItem("isAuth", false);
    }

    // Fetch location data

    localStorage.removeItem("parentUserRefferId");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("userObj");
    localStorage.removeItem("botModification");
    localStorage.removeItem("botStyle");
    localStorage.removeItem("botWorkFlowEdge");
    localStorage.removeItem("botWorkFlowNode");
    localStorage.removeItem("chatElement");
    localStorage.removeItem("chatNode");
    clearEmailVerificationFlowStorage({ clearPendingPlan: true });

    dispatch({ type: "LOGOUT" });
    navigate("/login");
  };

  /**
   * Updates the user object in state.
   * @param {Object} user - New user object.
   */
  const updateUser = (user) => {
    dispatch({
      type: "USER_UPDATE",
      payload: user,
      accessType: user.userType === 6 ? user.parentType : user.userType,
    });
  };

  /**
   * Updates company info in state.
   * @param {Object} payload - Company info.
   */
  const updateCompanyInfo = (payload) => {
    dispatch({ type: "SET_COMPANY_INFO", payload: payload });
  };

  /**
   * Updates Razorpay start status.
   * @param {boolean} payload - Status.
   */
  const updateIsRazorPayStart = (payload) => {
    dispatch({ type: "SET_IS_RAZOR_PAY_START", payload: payload });
  };

  /**
   * Checks if a JWT token is valid and not expired.
   * @param {string} accessToken - The JWT access token.
   * @returns {boolean} True if valid, false otherwise.
   */
  const isValidToken = (accessToken) => {
    if (!accessToken) {
      return false;
    }

    try {
      const decoded = jwtDecode(accessToken);
      const currentTime = Date.now() / 1000;
      return decoded.exp > Math.ceil(currentTime);
    } catch (error) {
      console.error("Token validation error:", error);
      return false;
    }
  };

  useEffect(() => {
    const initialise = async () => {
      const storedLocation = getStoredLocation();
      const hasStoredLocation =
        storedLocation.country && storedLocation.currency;

      if (
        hasStoredLocation &&
        isStoredLocationFresh(storedLocation.updatedAt)
      ) {
        dispatch({
          type: "SET_LOCATION",
          payload: {
            defaultCountry: storedLocation.country,
            defaultCurrency: storedLocation.currency,
          },
        });
      } else {
        const ipData = await getIpInfo();
        if (ipData && ipData?.countryName) {
          let currency = ipData?.currency?.code ?? "INR";
          dispatch({
            type: "SET_LOCATION",
            payload: {
              defaultCountry: ipData?.countryName,
              defaultCurrency: currency,
            },
          });
          setStoredLocation(ipData?.countryName, currency);
        } else if (hasStoredLocation) {
          dispatch({
            type: "SET_LOCATION",
            payload: {
              defaultCountry: storedLocation.country,
              defaultCurrency: storedLocation.currency,
            },
          });
        }
      }

      try {
        const accessToken = localStorage.getItem("accessToken");
        if (accessToken && isValidToken(accessToken)) {
          await me();
        } else {
          dispatch({
            type: "INITIALISE",
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        console.error("Initialisation error:", err);
        if (hasStoredLocation) {
          dispatch({
            type: "SET_LOCATION",
            payload: {
              defaultCountry: storedLocation.country,
              defaultCurrency: storedLocation.currency,
            },
          });
        } else {
          dispatch({
            type: "SET_LOCATION",
            payload: {
              defaultCountry: "India",
              defaultCurrency: "INR",
            },
          });
          setStoredLocation("India", "INR");
        }

        dispatch({
          type: "INITIALISE",
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialise();
  }, []);

  /**
   * Updates the global loader state.
   * @param {boolean} loader - Loader state.
   */
  const updateLoader = (loader) => {
    dispatch({ type: "LOADER_UPDATE", payload: loader });
  };

  /**
   * Updates email verification status.
   * @param {boolean|string} value - Verification status.
   */
  const updateEmailVerified = (value) => {
    console.log(value, "EMAIL_VERIFIED");
    dispatch({
      type: "EMAIL_VERIFIED",
      payload: value,
    });
  };

  /**
   * Creates subscription payload in state.
   * @param {Object} payload - Subscription payload.
   */
  const createSubscriptionPayload = (payload) => {
    dispatch({
      type: "CREATE_SUBSCRIPTION_PAYLOAD",
      payload: payload,
    });
  };

  /**
   * Sets live chat messages.
   * @param {Array} msg - List of messages.
   */
  const handleSetLiveChat = async (msg) => {
    dispatch({ type: "SET_LIVE_CHAT", payload: msg });
  };

  /**
   * Updates the takeover socket connection.
   * @param {Object} msg - Socket object.
   */
  const updateTakeOverSocketConnection = async (msg) => {
    dispatch({ type: "TAKEOVER_SOCKET_CONNECTION", payload: msg });
  };

  /**
   * Updates chat request availability status.
   * @param {boolean} value - Availability status.
   */
  const updateChatRequestAvailable = (value) => {
    dispatch({
      type: "SET_CHAT_REQUEST_AVAILABLE",
      payload: value,
    });
  };

  /**
   * Sets the current takeover chat.
   * @param {Object} value - Chat object.
   */
  const updateSetTakeOverChat = (value) => {
    dispatch({
      type: "SET_TAKEOVER_CHAT",
      payload: value,
    });
  };

  if (!state.isInitialised) {
    return <SplashScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        updateUser,
        register,
        superLogin,
        me,
        updateLoader,
        updateEmailVerified,
        createSubscriptionPayload,
        deleteAccount,
        updateCompanyInfo,
        updateIsRazorPayStart,
        handleSetLiveChat,
        updateTakeOverSocketConnection,
        updateChatRequestAvailable,
        updateSetTakeOverChat,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
